import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { ServicePageSEO } from '@/components/seo';
import { ServiceFAQSection } from '@/components/faq';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ServicePage - Displays a single service with full SEO
 * Fetches data from GET /api/public/services/{slug}
 */
const ServicePage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchService = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/public/services/${slug}`);
        setService(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching service:', err);
        setError(err.response?.data?.detail || 'Failed to load service');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchService();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="service-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading service...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="service-error">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="service-not-found">
        <div>Service not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="service-page">
      {/* SEO Component */}
      <ServicePageSEO 
        service={service} 
        baseUrl={BACKEND_URL?.replace('/api', '')}
      />
      
      {/* Page Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="service-title">
            {service.name}
          </h1>
          {service.category && (
            <span className="inline-block bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full" data-testid="service-category">
              {service.category}
            </span>
          )}
        </header>

        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <p className="text-gray-700 text-lg leading-relaxed" data-testid="service-description">
            {service.description}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Pricing</h2>
          <div className="text-3xl font-bold text-green-600" data-testid="service-price">
            ${service.base_price?.toFixed(2) || '0.00'}
          </div>
          <p className="text-gray-500 mt-2">Starting price</p>
          
          <a
            href={service.cta_url || '/contact'}
            className="mt-6 block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
            data-testid="service-cta-button"
          >
            {service.cta_label || 'Get Started'}
          </a>
        </div>

        {/* FAQ Section */}
        <ServiceFAQSection 
          serviceSlug={slug}
          serviceName={service.name}
          limit={5}
        />
      </div>
    </div>
  );
};

export default ServicePage;
